import React, { useState, useEffect } from 'react'
import { Modal as ContentModal } from 'react-bootstrap'
import PropTypes from 'prop-types'

import Button from '../Button'
import DropdownButton from '../DropdownButton'
// import ToolTip from '../ToolTip'

export const DocumentPreviewHeaders = ({
  onHide,
  file,
  setCurrentWidth,
  currentOrientation,
  setCurrentOrientation,
  setDraggableImageElements,
  pageNumber,
  toggleNavbar,
  setPageNumber,
  totalPageNumbers,
  isOriginalDocLandscape,
  leftNavbarOpen
}) => {
  const [widthDisplay, setWidthDisplay] = useState(100)

  useEffect(() => {
    // Function to handle screen resize
    const handleResize = () => fixZoom()
    // Attach the event listener to the window resize event
    window.addEventListener('resize', handleResize)
    // Call handleResize initially to set styles on component mount
    handleResize()
    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [fixZoom]) // Empty dependency array means this effect runs once on mount

  const renderIcon = (file) => {
    if (file.type === 'application/pdf') return 'fa-file-pdf'
    if (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/gif') return 'fa-file-image'
    return 'fa-file-exclamation'
  }

  const zoomIn = () => {
    if (file.type === 'application/pdf') {
      if (widthDisplay < 200) {
        setCurrentWidth((prevWidth) => prevWidth + 10)
        setWidthDisplay((prevWidth) => prevWidth + 10)
        const canvasElements = document.querySelectorAll('.page')
        visibleElementCheck('zoom-in', canvasElements)
      }
    } else {
      if (widthDisplay < 200) {
        setCurrentWidth((prevWidth) => prevWidth + 10)
        setWidthDisplay((prevWidth) => prevWidth + 10)
        const canvasElements = document.querySelector('#ImageContainer')
        canvasElements.style.transform += 'translate(-5%, -5%)'
      }
    }
  }

  const zoomOut = () => {
    if (file.type === 'application/pdf') {
      if (widthDisplay > 50) {
        setCurrentWidth((prevWidth) => prevWidth - 10)
        setWidthDisplay((prevWidth) => prevWidth - 10)
        const canvasElements = document.querySelectorAll('.page')
        visibleElementCheck('zoom-out', canvasElements)
        fixZoom()
      }
    } else {
      if (widthDisplay > 50) {
        setCurrentWidth((prevWidth) => prevWidth - 10)
        setWidthDisplay((prevWidth) => prevWidth - 10)
        const canvasElements = document.querySelector('#ImageContainer')
        canvasElements.style.transform += 'translate(5%, 5%)'
      }
    }
  }

  const rotateOrientation = () => {
    if (file.type !== 'application/pdf') {
      const canvasElements = document.querySelector('#ImageContainer')
      setCurrentOrientation((prevOrientation) => prevOrientation + 90)
      canvasElements.style.transition = 'none'
    } else {
      const canvasElements = document.querySelectorAll('.page')
      const canvasMiniElements = document.querySelectorAll('.pageMini')
      setCurrentOrientation((prevOrientation) => prevOrientation + 90)
      canvasElements.forEach((element, index) => {
        element.style.transition = 'none' // Disable transition
        element.style.transform = `rotate(${currentOrientation + 90}deg)`
        const elementRect = element.getBoundingClientRect()
        const isPageLandScape = elementRect.width > elementRect.height
        if (isOriginalDocLandscape && !isPageLandScape) {
          element.style.margin = `${200 * (widthDisplay / 100)}px 200px 200px`
        } else {
          element.style.margin = '10px'
          if (index === 0) {
            // Check if the first element is cut off on top. If so, add margin
            if (elementRect.top < document.documentElement.scrollTop) {
              element.style.marginTop = '20px'
              console.debug('file: index.js:198 ༻༺ canvasElements.forEach ༻༺ 125px:')
            } else {
              element.style.marginTop = '10px'
            }
          } else {
            element.style.marginTop = '-300px'
          }
        }
        if (index < canvasElements.length - 1) {
          const nextPage = canvasElements[index + 1]
          const pageBottom = element.getBoundingClientRect().right
          const nextPageTop = nextPage.getBoundingClientRect().left
          const overlap = nextPageTop - pageBottom
          const elementRect = element.getBoundingClientRect()
          const isPageLandScape = elementRect.width < elementRect.height
          const overlapAmount = isOriginalDocLandscape && isPageLandScape ? 50 : 20
          if (overlap < overlapAmount) {
            if (!isOriginalDocLandscape && isPageLandScape) {
              element.style.marginBottom = '20px'
            } else {
              const newMargin = Math.abs(overlap)
              const currentMargin = parseInt(getComputedStyle(element).marginBottom, 10) || 0
              const dynamicMinMargin = Math.ceil((((widthDisplay / 100)) * newMargin) / 4)
              const minMargin = Math.max(currentMargin, dynamicMinMargin)
              const finalMargin = Math.max(currentMargin - newMargin, minMargin)
              element.style.marginBottom = `${finalMargin}px`
            }
          }
        }
      })
      fixZoom()
      canvasMiniElements.forEach((element) => {
        element.style.transition = 'none' // Disable transition
        element.style.transform = `rotate(${currentOrientation + 90}deg)`
      })
    }
  }

  const fixZoom = useCallback(() => {
    const canvasElements = document.querySelectorAll('.page')
    canvasElements.forEach((element, index) => {
      if (index < canvasElements.length - 1) {
        const nextPage = canvasElements[index + 1]
        const pageBottom = element.getBoundingClientRect().bottom
        const nextPageTop = nextPage.getBoundingClientRect().top
        const overlap = nextPageTop - pageBottom
        const elementRect = element.getBoundingClientRect()
        const isPageLandScape = elementRect.width > elementRect.height
        const overlapAmount = isOriginalDocLandscape && !isPageLandScape ? 50 : 20
        if (overlap < overlapAmount) {
          // Adjust the margin dynamically based on the overlap
          const newMargin = Math.abs(overlap)
          const currentMargin = parseInt(getComputedStyle(element).marginBottom, 10) || 0
          element.style.marginBottom = `${currentMargin + newMargin + 10}px`
        } else {
          if (overlap > overlapAmount) {
            const newMargin = Math.abs(overlap)
            const currentMargin = parseInt(getComputedStyle(element).marginBottom, 10) || 0
            const minMargin = 10 // minimum
            const finalMargin = Math.max(currentMargin - newMargin, minMargin)
            element.style.marginBottom = `${finalMargin + 10}px`
          }
        }
      }
    })
  })

  const visibleElementCheck = (action, canvasElements) => {
    canvasElements.forEach((element, index) => {
      const currentWidth = element.clientWidth
      const newWidth = currentWidth * (action === 'zoom-in' ? 1.1 : 0.9)
      element.style.transition = 'none' // Disable transition
      element.style.width = `${newWidth}px` // Set the new width
      const modalElement = document.querySelector('.document-preview-modal')
      const contentElement = document.getElementById('pages')
      const isElementCutOff = () => {
        const modalRect = modalElement.getBoundingClientRect()
        const contentRect = contentElement.getBoundingClientRect()
        const isLeftCutOff = contentRect.left < modalRect.left
        return isLeftCutOff
      }
      const adjustWidthAndPadding = () => {
        const elementCutOff = isElementCutOff()
        if (elementCutOff) {
          const paddingAmount = modalElement.getBoundingClientRect().left - contentElement.getBoundingClientRect().left
          contentElement.style.paddingLeft = `${paddingAmount}px`
          if (action === 'zoom-out') {
            contentElement.style.width = `${contentElement.offsetWidth + paddingAmount}px`
          }
        } else {
          contentElement.style.paddingLeft = '0'
        }
      }
      adjustWidthAndPadding()
      if (index < canvasElements.length - 1) {
        const nextPage = canvasElements[index + 1]
        const pageBottom = element.getBoundingClientRect().bottom
        const nextPageTop = nextPage.getBoundingClientRect().top
        const overlap = nextPageTop - pageBottom
        const elementRect = element.getBoundingClientRect()
        const isPageLandScape = elementRect.width > elementRect.height
        const overlapAmount = isOriginalDocLandscape && !isPageLandScape ? 50 : 20
        if (action === 'zoom-in') {
          if (overlap < overlapAmount) {
            const newMargin = Math.abs(overlap)
            const currentMargin = parseInt(getComputedStyle(element).marginBottom, 10) || 0
            element.style.marginBottom = `${currentMargin + newMargin + 50}px`
          }
        } else {
          if (overlap > overlapAmount) {
            const newMargin = Math.abs(overlap)
            const currentMargin = parseInt(getComputedStyle(element).marginBottom, 10) || 0
            const minMargin = 10 // minimum
            const finalMargin = Math.max(currentMargin - newMargin, minMargin)
            element.style.marginBottom = `${finalMargin + 10}px`
          }
        }
      }
    })
  }

  const resetPreview = () => {
    const width =
      window.innerWidth >= 1400 ? 55
        : window.innerWidth >= 1200 ? 65
          : window.innerWidth >= 992 ? 75
            : window.innerWidth >= 768 ? 85 : 100
    setCurrentWidth(width)
    setWidthDisplay(100)
    setCurrentOrientation(0)

    if (file.type !== 'application/pdf') {
      setDraggableImageElements({ img: { top: 10, left: 10 } })
    } else {
      const canvasElements = document.querySelectorAll('.page')
      const canvasMiniElements = document.querySelectorAll('.pageMini')
      canvasElements.forEach((element) => {
        element.style.transition = 'none' // Disable transition
        element.style.transform = 'rotate(0deg)'
        let initialWidthPercentage
        if (window.innerWidth <= 576) {
          initialWidthPercentage = 85
        } else if (window.innerWidth <= 767 || (window.innerWidth >= 768 && window.innerWidth <= 991)) {
          initialWidthPercentage = 90
        } else {
          initialWidthPercentage = 95
        }
        element.style.cssText = `transition: none; width: ${initialWidthPercentage}%;`
        element.style.paddingLeft = '0px'
        element.style.margin = '10px'
      })
      canvasMiniElements.forEach((element) => {
        element.style.transition = 'none' // Disable transition
        element.style.transform = 'rotate(0deg)'
      })
      const contentElement = document.getElementById('pages')
      contentElement.style.paddingLeft = '0px'
      const currentPaddingLeft = parseInt(getComputedStyle(contentElement).marginLeft, 10) || 0
      if (leftNavbarOpen && currentPaddingLeft === 0) {
        contentElement.style.marginLeft = '150px'
      }
    }
  }

  const fileDownload = () => {
    const blob = new Blob([file], { type: file.type })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name
    anchor.click()
  }

  return (
    <ContentModal.Header closeButton={false} bsPrefix='modal-header row flex-sm-row document-preview-header p-2 text-white'>

      {/* LEFT TOPNAV */}
      <div className='col-md-4 d-none d-md-flex align-items-center'>

        {/* LEFT NAV BUTTON TOGGLE */}
        {file.type === 'application/pdf' &&
          <Button ghost ariaLabel='Close' onClick={() => toggleNavbar()} className='p-1'>
            {/* <ToolTip text='Grid View'>
              <span className='fas fa-table-columns text-white pointer mx-2' />
            </ToolTip> */}
          </Button>
        }

        {/* DIVIDER */}
        {file.type === 'application/pdf' &&
          <div className='border border-end border-light mx-2' style={{ height: '1.6rem' }} />
        }

        {/* FILE TITLE */}
        <span className={`fas ${renderIcon(file)} mx-2`} aria-hidden='true' />
        {file.name && <span className='text-truncate mx-2' aria-label={file.name}>{file.name}</span>}
      </div>

      {/* MIDDLE TOPNAV OPTIONS */}
      <div className='col-6 col-sm-8 col-md-5 d-flex justify-content-start justify-content-sm-start justify-content-md-center align-items-center'>

        {/* PAGE NUMBER */}
        {file.type === 'application/pdf' &&
          <div className='d-flex justify-content-center align-items-center'>
            <Button
              ghost
              id='set-prev-page'
              className='p-1 mx-2 d-none d-sm-block'
              ariaLabel='Previous Page'
              disabled={parseInt(pageNumber) === 1}
              onClick={() => {
                const targetElementId = `canvasPage_${parseInt(pageNumber) - 1}`
                const element = document.getElementById(targetElementId)
                if (element) {
                  // Delay the scrollIntoView until after the state has been updated
                  setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' })
                  }, 0)
                  setPageNumber((prev) => prev - 1)
                }
              }}
            >
              {/* <ToolTip text='Previous Page'>
                <span className='fas fa-chevron-left text-white pointer mx-2' />
              </ToolTip> */}
            </Button>

            <span className='d-none d-lg-flex me-2'>Page:</span>
            <input
              type='text'
              name='page-number'
              aria-label='page'
              className='border-0 text-white text-center py-0'
              style={{
                width: `${(pageNumber?.toString().length + 1) * 13}px`,
                backgroundColor: '#6E6E6E'
              }}
              onChange={(e) => {
                const enteredValue = e.target.value.trim()
                if (enteredValue !== '') {
                  const enteredPage = (enteredValue === '0') ? 1 : parseInt(enteredValue, 10)
                  setPageNumber(enteredPage)
                } else {
                  setPageNumber('')
                }
              }}
              onBlur={(e) => {
                const enteredValue = e.target.value
                if (/^\d*$/.test(enteredValue)) {
                  const enteredPage = enteredValue === ''
                    ? 1
                    : parseInt(enteredValue, 10) > totalPageNumbers
                      ? totalPageNumbers
                      : parseInt(enteredValue, 10)
                  if (enteredValue !== '' || enteredPage !== 0) {
                    setPageNumber(enteredPage)
                  }
                  const element = document.getElementById(`canvasPage_${enteredPage}`)
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' })
                  }
                }
              }}
              value={pageNumber}
            />
            <span className='mx-2'>/</span>{totalPageNumbers}
            <Button
              ghost
              id='set-next-page'
              className='p-1 mx-2 d-none d-sm-block'
              ariaLabel='Next Page'
              disabled={parseInt(pageNumber) === parseInt(totalPageNumbers)}
              onClick={() => {
                const targetElementId = `canvasPage_${parseInt(pageNumber) + 1}`
                const element = document.getElementById(targetElementId)
                if (element) {
                  // Delay the scrollIntoView until after the state has been updated
                  setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' })
                  }, 0)
                  setPageNumber((prev) => prev + 1)
                }
              }}
            >
              {/* <ToolTip text='Next Page'>
                <span className='fas fa-chevron-right text-white pointer mx-2' />
              </ToolTip> */}
            </Button>

          </div>
        }

        {/* RIGHT TOPNAV */}
        <div className='d-flex align-items-center'>
          {/* ZOOM OUT */}
          <Button ghost id='zoom-out' onClick={zoomOut} className={`p-1 ${file.type === 'application/pdf' ? 'ms-3' : ''}`.trim()} ariaLabel='zoomOut'>
            {/* <ToolTip text='Zoom Out'>
              <span className='fas fa-magnifying-glass-minus text-white pointer mx-2' />
            </ToolTip> */}
          </Button>

          {/* CURRENT ZOOM */}
          <span id='zoom-percent' className='d-none d-lg-flex mx-2'>{widthDisplay}</span>

          {/* ZOOM IN */}
          <Button ghost id='zoom-in' onClick={zoomIn} className='p-1 me-3' ariaLabel='zoomIn'>
            {/* <ToolTip text='Zoom In'>
              <span className='fas fa-magnifying-glass-plus text-white pointer mx-2' />
            </ToolTip> */}
          </Button>
        </div>
      </div>

      <div className='col-6 col-sm-4 col-md-3 d-flex justify-content-end justify-content-sm-end align-items-center'>

        {/* ROTATE IMAGE */}
        <Button ghost id='rotate-right' onClick={rotateOrientation} className='p-1' ariaLabel='rotate-right preview'>
          {/* <ToolTip text='Rotate'>
            <span className='fas fa-rotate-right text-white pointer mx-2' />
          </ToolTip> */}
        </Button>

        {/* DROPDOWN BUTTON -SM- */}
        {<DropdownButton
          ghost
          className='d-block d-lg-none btn-sm text-white p-1'
          options={[
            {
              content: <span><span className='fas fa-down-left-and-up-right-to-center me-3'></span>Reset Orientation</span>,
              disabled: false,
              onClick: () => resetPreview()
            },
            {
              content: <span><span className='fas fa-download me-3'></span>Download</span>,
              disabled: false,
              onClick: () => fileDownload()
            }
          ]}
        >
          {/* <ToolTip text='More Options'>
            <span className='fas fa-ellipsis text-white pointer mx-2'></span>
          </ToolTip> */}
        </DropdownButton>
        }

        {/* RESET ZOOM AND ORIENTATION */}
        <Button ghost id='zoom-reset' onClick={resetPreview} className='p-1 d-none d-lg-block' ariaLabel='resetZoom'>
          {/* <ToolTip text='Reset Settings'>
            <span className='fas fa-down-left-and-up-right-to-center text-white pointer mx-2' />
          </ToolTip> */}
        </Button>

        {/* DOWNLOAD FILE */}
        <Button ghost id='file-download' onClick={fileDownload} className='p-1 d-none d-lg-block' ariaLabel='downloadFile'>
          {/* <ToolTip text='Download'>
            <span className='fas fa-download text-white pointer mx-2' />
          </ToolTip> */}
        </Button>

        {/* CLOSE MODAL */}
        <Button ghost ariaLabel='Close' onClick={() => onHide()} className='p-1'>
          {/* <ToolTip text='Close'>
            <span className='fas fa-xmark text-white pointer mx-2' />
          </ToolTip> */}
        </Button>
      </div>
    </ContentModal.Header>
  )
}

DocumentPreviewHeaders.propTypes = {
  onHide: PropTypes.func,
  file: PropTypes.object,
  setCurrentWidth: PropTypes.func,
  currentOrientation: PropTypes.number,
  setCurrentOrientation: PropTypes.func,
  setDraggableImageElements: PropTypes.func,
  pageNumber: PropTypes.number,
  toggleNavbar: PropTypes.func,
  setPageNumber: PropTypes.func,
  totalPageNumbers: PropTypes.number,
  isOriginalDocLandscape: PropTypes.bool,
  leftNavbarOpen: PropTypes.bool
}

export default DocumentPreviewHeaders
